import type { Row } from './biData';

// ============================================================
// Zifek BI — filtres interactifs et cross-filtering.
//  • Filtres d'attributs (catégorie, ville, produit…) ajoutés en
//    cliquant sur une barre/part d'un graphique.
//  • Filtre de date avec périodes prédéfinies (aujourd'hui, ce mois…).
//  • Ces filtres s'appliquent à TOUS les graphiques du dashboard.
// ============================================================

export interface BiFilterItem {
  id: string;
  column: string;
  value: string;
}

export interface BiDateFilter {
  column: string;
  from: string; // yyyy-mm-dd
  to: string;   // yyyy-mm-dd
  preset: string;
}

export interface BiFilterState {
  filters: BiFilterItem[];
  date: BiDateFilter | null;
}

export const EMPTY_FILTERS: BiFilterState = { filters: [], date: null };

export interface DatePreset {
  id: string;
  label: string;
}

export const DATE_PRESETS: DatePreset[] = [
  { id: 'today', label: "Aujourd'hui" },
  { id: 'yesterday', label: 'Hier' },
  { id: 'this_week', label: 'Cette semaine' },
  { id: 'last_week', label: 'Semaine précédente' },
  { id: 'this_month', label: 'Ce mois' },
  { id: 'last_month', label: 'Mois précédent' },
  { id: 'this_year', label: 'Cette année' },
  { id: 'last_year', label: 'Année précédente' },
];

function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Calcule la plage [from, to] d'une période prédéfinie. */
export function datePresetRange(preset: string): { from: string; to: string } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case 'today':
      return { from: toIso(today), to: toIso(today) };
    case 'yesterday': {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return { from: toIso(y), to: toIso(y) };
    }
    case 'this_week': {
      const day = (today.getDay() + 6) % 7; // lundi = 0
      const start = new Date(today);
      start.setDate(today.getDate() - day);
      return { from: toIso(start), to: toIso(today) };
    }
    case 'last_week': {
      const day = (today.getDay() + 6) % 7;
      const thisStart = new Date(today);
      thisStart.setDate(today.getDate() - day);
      const start = new Date(thisStart);
      start.setDate(thisStart.getDate() - 7);
      const end = new Date(thisStart);
      end.setDate(thisStart.getDate() - 1);
      return { from: toIso(start), to: toIso(end) };
    }
    case 'this_month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: toIso(start), to: toIso(today) };
    }
    case 'last_month': {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: toIso(start), to: toIso(end) };
    }
    case 'this_year': {
      const start = new Date(today.getFullYear(), 0, 1);
      return { from: toIso(start), to: toIso(today) };
    }
    case 'last_year': {
      const start = new Date(today.getFullYear() - 1, 0, 1);
      const end = new Date(today.getFullYear() - 1, 11, 31);
      return { from: toIso(start), to: toIso(end) };
    }
    default:
      return { from: '', to: '' };
  }
}

/** Convertit une valeur (texte/date) en timestamp, ou null si non parsable. */
function parseDateValue(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const s = String(value).trim();
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (m) {
    const [, d, mo, y] = m;
    const year = y.length === 2 ? 2000 + Number(y) : Number(y);
    return new Date(year, Number(mo) - 1, Number(d)).getTime();
  }
  const t = Date.parse(s.replace(' ', 'T'));
  return Number.isNaN(t) ? null : t;
}

/** Applique les filtres (attributs + date) à un jeu de lignes. */
export function applyBiFilters(rows: Row[], state: BiFilterState): Row[] {
  let out = rows;
  if (state.filters.length > 0) {
    out = out.filter((r) => state.filters.every((f) => String(r[f.column] ?? '(vide)') === f.value));
  }
  if (state.date && state.date.column && (state.date.from || state.date.to)) {
    const from = state.date.from ? new Date(`${state.date.from}T00:00:00`).getTime() : -Infinity;
    const to = state.date.to ? new Date(`${state.date.to}T23:59:59`).getTime() : Infinity;
    const col = state.date.column;
    out = out.filter((r) => {
      const t = parseDateValue(r[col]);
      return t !== null && t >= from && t <= to;
    });
  }
  return out;
}

/** Clé unique d'un filtre (pour l'ajout / la suppression). */
export function filterKey(column: string, value: string): string {
  return `${column}::${value}`;
}